import { api } from "./api";

export type ValuationSource =
  | "KAUFPREIS"
  | "GUTACHTEN"
  | "EIGENE_SCHAETZUNG"
  | "VERKEHRSWERT_BERICHT";

export interface Property {
  id: string;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  purchaseDate: string;
  purchasePriceCents: number;
  acquisitionCostsCents: number;
  buildingShareBp: number;
  afaRateBp: number;
  afaStartDate: string;
  accountId: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Loan {
  id: string;
  propertyId: string;
  name: string;
  principalCents: number;
  startDate: string;
  annualInterestRateBp: number;
  monthlyPaymentCents: number;
  interestPhases: InterestPhase[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Prepayment {
  id: string;
  loanId: string;
  date: string;
  amountCents: number;
  notes: string | null;
  createdAt: string;
}

export interface Valuation {
  id: string;
  propertyId: string;
  stichtag: string;
  valueCents: number;
  source: ValuationSource;
  notes: string | null;
  createdAt: string;
}

export interface ScheduleEntry {
  month: number;
  date: string;
  principalAtStart: number;
  interestCents: number;
  principalCents: number;
  prepaymentCents: number;
  paymentCents: number;
  principalAtEnd: number;
  interestRateBp: number;
}

export interface ScheduleOverride {
  id: string;
  loanId: string;
  month: string;
  interestCents: number | null;
  principalCents: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertScheduleOverrideInput {
  month: string;
  interestCents?: number | null;
  principalCents?: number | null;
  notes?: string | null;
}

export interface YieldResult {
  bruttoBp: number | null;
  nettoBp: number | null;
  equityReturnBp: number | null;
}

export interface AfaResult {
  annualAfaCents: number;
  accumulatedAfaCents: number;
  bookValueCents: number;
}

export interface ListPropertiesFilters {
  active?: boolean;
}

export interface CreatePropertyInput {
  name: string;
  street: string;
  postalCode: string;
  city: string;
  purchaseDate: string;
  purchasePriceCents: number;
  acquisitionCostsCents: number;
  buildingShareBp: number;
  afaRateBp: number;
  afaStartDate: string;
  accountId?: string;
  notes?: string;
}

export type UpdatePropertyInput = Partial<
  CreatePropertyInput & { accountId: string | null; notes: string | null; isActive: boolean }
>;

export interface CreateLoanInput {
  name: string;
  principalCents: number;
  startDate: string;
  annualInterestRateBp: number;
  monthlyPaymentCents: number;
}

export type UpdateLoanInput = Partial<
  CreateLoanInput & {
    isActive: boolean;
  }
>;

export interface CreatePrepaymentInput {
  date: string;
  amountCents: number;
  notes?: string;
}

export interface CreateValuationInput {
  stichtag: string;
  valueCents: number;
  source: ValuationSource;
  notes?: string;
}

export type RecurringCostCategory =
  | "HAUSGELD"
  | "VERSICHERUNG"
  | "INSTANDHALTUNG"
  | "GRUNDSTEUER"
  | "SONSTIGES";
export type OneTimeEntryCategory =
  | "REPARATUR"
  | "KAUTION"
  | "NACHZAHLUNG"
  | "SONDEREINNAHME"
  | "SONDERAUSGABE";

export interface Tenant {
  id: string;
  propertyId: string;
  name: string;
  unitLabel: string;
  moveInDate: string;
  moveOutDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RentChange {
  id: string;
  tenantId: string;
  effectiveFrom: string;
  coldRentCents: number;
  sideCostsAdvanceCents: number;
  notes: string | null;
  createdAt: string;
}

export interface RecurringCost {
  id: string;
  propertyId: string;
  category: RecurringCostCategory;
  effectiveFrom: string;
  monthlyAmountCents: number;
  notes: string | null;
  createdAt: string;
}

export interface OneTimeEntry {
  id: string;
  propertyId: string;
  date: string;
  amountCents: number;
  category: OneTimeEntryCategory;
  description: string;
  notes: string | null;
  createdAt: string;
}

export interface MonthlyCashflow {
  coldRentCents: number;
  sideCostsAdvanceCents: number;
  hausgeldCents: number;
  versicherungCents: number;
  instandhaltungCents: number;
  grundsteuerCents: number;
  sonstigesCents: number;
  expenseCents: number;
  interestCents: number;
  principalCents: number;
  prepaymentCents: number;
  oneTimeIncomeCents: number;
  oneTimeExpenseCents: number;
  netCents: number;
  bankNetCents: number;
}

export interface MonthlyCashflowResponse {
  months: { month: string; cashflow: MonthlyCashflow }[];
}

export interface CashflowOverviewResponse {
  currentMonth: string;
  monthBankNetCents: number;
  monthNetCents: number;
  ytdBankNetCents: number;
  ytdNetCents: number;
  lifetimeBankNetCents: number;
  lifetimeNetCents: number;
  lifetimeMonthCount: number;
}

export interface CreateTenantInput {
  name: string;
  unitLabel: string;
  moveInDate: string;
  notes?: string;
  initialRent: { coldRentCents: number; sideCostsAdvanceCents: number };
}

export interface UpdateTenantInput {
  name?: string;
  unitLabel?: string;
  moveOutDate?: string | null;
  notes?: string | null;
}

export interface CreateRentChangeInput {
  effectiveFrom: string;
  coldRentCents: number;
  sideCostsAdvanceCents: number;
  notes?: string;
}

export interface CreateRecurringCostInput {
  category: RecurringCostCategory;
  effectiveFrom: string;
  monthlyAmountCents: number;
  notes?: string;
}

export interface CreateOneTimeEntryInput {
  date: string;
  amountCents: number;
  category: OneTimeEntryCategory;
  description: string;
  notes?: string;
}

export interface PropertyDetail extends Property {
  account: { id: string; name: string; isActive: boolean } | null;
  loans: (Loan & { prepayments: Prepayment[] })[];
  valuations: Valuation[];
  afa: AfaResult;
  equityCents: number;
  tenants: (Tenant & { rentChanges: RentChange[] })[];
  recurringCosts: RecurringCost[];
}

export interface PortfolioSummary {
  totalMarketValueCents: number;
  totalRemainingDebtCents: number;
  totalEquityCents: number;
  totalYtdBankNetCents: number;
  totalLifetimeBankNetCents: number;
  propertyCount: number;
  hasStaleValuation: boolean;
}

function qs(filters: ListPropertiesFilters): string {
  const sp = new URLSearchParams();
  if (filters.active !== undefined) sp.set("active", String(filters.active));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export interface InterestPhase {
  id: string;
  startDate: string;
  annualInterestRateBp: number;
  monthlyPaymentCents: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInterestPhaseInput {
  startDate: string;
  annualInterestRateBp: number;
  monthlyPaymentCents: number;
  notes?: string;
}

export interface UpdateInterestPhaseInput {
  startDate?: string;
  annualInterestRateBp?: number;
  monthlyPaymentCents?: number;
  notes?: string | null;
}

export interface UpdateRentChangeInput {
  effectiveFrom?: string;
  coldRentCents?: number;
  sideCostsAdvanceCents?: number;
  notes?: string | null;
}

export interface UpdatePrepaymentInput {
  date?: string;
  amountCents?: number;
  notes?: string | null;
}

export interface UpdateValuationInput {
  stichtag?: string;
  valueCents?: number;
  source?: ValuationSource;
  notes?: string | null;
}

export interface UpdateRecurringCostInput {
  category?: RecurringCostCategory;
  effectiveFrom?: string;
  monthlyAmountCents?: number;
  notes?: string | null;
}

export interface UpdateOneTimeEntryInput {
  date?: string;
  amountCents?: number;
  category?: OneTimeEntryCategory;
  description?: string;
  notes?: string | null;
}

export const realestateApi = {
  list: (filters: ListPropertiesFilters = {}) =>
    api<{ properties: Property[] }>(`/realestate${qs(filters)}`),
  summary: () => api<PortfolioSummary>("/realestate/summary"),
  get: (id: string) => api<PropertyDetail>(`/realestate/${id}`),
  create: (input: CreatePropertyInput) =>
    api<Property>("/realestate", { method: "POST", body: input }),
  update: (id: string, input: UpdatePropertyInput) =>
    api<Property>(`/realestate/${id}`, { method: "PATCH", body: input }),
  softDelete: (id: string) => api<void>(`/realestate/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) => api<void>(`/realestate/${id}?force=true`, { method: "DELETE" }),

  schedule: (propertyId: string, loanId: string) =>
    api<{ schedule: ScheduleEntry[]; overrides: ScheduleOverride[] }>(
      `/realestate/${propertyId}/amortization-schedule?loanId=${loanId}`,
    ),
  upsertScheduleOverride: (
    propertyId: string,
    loanId: string,
    input: UpsertScheduleOverrideInput,
  ) =>
    api<ScheduleOverride>(`/realestate/${propertyId}/loans/${loanId}/schedule-overrides`, {
      method: "POST",
      body: input,
    }),
  listScheduleOverrides: (propertyId: string, loanId: string) =>
    api<{ overrides: ScheduleOverride[] }>(
      `/realestate/${propertyId}/loans/${loanId}/schedule-overrides`,
    ),
  deleteScheduleOverride: (propertyId: string, loanId: string, overrideId: string) =>
    api<void>(`/realestate/${propertyId}/loans/${loanId}/schedule-overrides/${overrideId}`, {
      method: "DELETE",
    }),
  cashflow: (propertyId: string, year: number) =>
    api<MonthlyCashflowResponse>(`/realestate/${propertyId}/cashflow?year=${year}`),
  cashflowOverview: (propertyId: string) =>
    api<CashflowOverviewResponse>(`/realestate/${propertyId}/cashflow-overview`),
  yields: (propertyId: string, year: number) =>
    api<YieldResult>(`/realestate/${propertyId}/yields?year=${year}`),
  afa: (propertyId: string, asOf?: string) =>
    api<AfaResult>(`/realestate/${propertyId}/afa${asOf ? `?asOf=${asOf}` : ""}`),
  equity: (propertyId: string, asOf?: string) =>
    api<{ equityCents: number }>(`/realestate/${propertyId}/equity${asOf ? `?asOf=${asOf}` : ""}`),

  addLoan: (propertyId: string, input: CreateLoanInput) =>
    api<Loan>(`/realestate/${propertyId}/loans`, { method: "POST", body: input }),
  updateLoan: (propertyId: string, loanId: string, input: UpdateLoanInput) =>
    api<Loan>(`/realestate/${propertyId}/loans/${loanId}`, { method: "PATCH", body: input }),
  softDeleteLoan: (propertyId: string, loanId: string) =>
    api<void>(`/realestate/${propertyId}/loans/${loanId}`, { method: "DELETE" }),
  hardDeleteLoan: (propertyId: string, loanId: string) =>
    api<void>(`/realestate/${propertyId}/loans/${loanId}?force=true`, { method: "DELETE" }),

  addPrepayment: (propertyId: string, loanId: string, input: CreatePrepaymentInput) =>
    api<Prepayment>(`/realestate/${propertyId}/loans/${loanId}/prepayments`, {
      method: "POST",
      body: input,
    }),
  updatePrepayment: (
    propertyId: string,
    loanId: string,
    prepaymentId: string,
    input: UpdatePrepaymentInput,
  ) =>
    api<Prepayment>(`/realestate/${propertyId}/loans/${loanId}/prepayments/${prepaymentId}`, {
      method: "PATCH",
      body: input,
    }),
  deletePrepayment: (propertyId: string, loanId: string, prepaymentId: string) =>
    api<void>(`/realestate/${propertyId}/loans/${loanId}/prepayments/${prepaymentId}`, {
      method: "DELETE",
    }),

  listInterestPhases: (propertyId: string, loanId: string) =>
    api<{ phases: InterestPhase[] }>(`/realestate/${propertyId}/loans/${loanId}/interest-phases`),
  addInterestPhase: (propertyId: string, loanId: string, input: CreateInterestPhaseInput) =>
    api<InterestPhase>(`/realestate/${propertyId}/loans/${loanId}/interest-phases`, {
      method: "POST",
      body: input,
    }),
  updateInterestPhase: (
    propertyId: string,
    loanId: string,
    phaseId: string,
    input: UpdateInterestPhaseInput,
  ) =>
    api<InterestPhase>(`/realestate/${propertyId}/loans/${loanId}/interest-phases/${phaseId}`, {
      method: "PATCH",
      body: input,
    }),
  deleteInterestPhase: (propertyId: string, loanId: string, phaseId: string) =>
    api<void>(`/realestate/${propertyId}/loans/${loanId}/interest-phases/${phaseId}`, {
      method: "DELETE",
    }),

  listValuations: (propertyId: string) =>
    api<{ valuations: Valuation[] }>(`/realestate/${propertyId}/valuations`),
  addValuation: (propertyId: string, input: CreateValuationInput) =>
    api<Valuation>(`/realestate/${propertyId}/valuations`, { method: "POST", body: input }),
  updateValuation: (propertyId: string, valuationId: string, input: UpdateValuationInput) =>
    api<Valuation>(`/realestate/${propertyId}/valuations/${valuationId}`, {
      method: "PATCH",
      body: input,
    }),
  deleteValuation: (propertyId: string, valuationId: string) =>
    api<void>(`/realestate/${propertyId}/valuations/${valuationId}`, { method: "DELETE" }),

  // ---- Tenants ----
  listTenants: (propertyId: string, query?: { active?: boolean }) => {
    const qs2 = query?.active !== undefined ? `?active=${query.active}` : "";
    return api<{ tenants: (Tenant & { rentChanges: RentChange[] })[] }>(
      `/realestate/${propertyId}/tenants${qs2}`,
    );
  },
  addTenant: (propertyId: string, input: CreateTenantInput) =>
    api<Tenant & { rentChanges: RentChange[] }>(`/realestate/${propertyId}/tenants`, {
      method: "POST",
      body: input,
    }),
  updateTenant: (propertyId: string, tenantId: string, input: UpdateTenantInput) =>
    api<Tenant>(`/realestate/${propertyId}/tenants/${tenantId}`, {
      method: "PATCH",
      body: input,
    }),
  endTenancy: (propertyId: string, tenantId: string) =>
    api<void>(`/realestate/${propertyId}/tenants/${tenantId}`, { method: "DELETE" }),
  hardDeleteTenant: (propertyId: string, tenantId: string) =>
    api<void>(`/realestate/${propertyId}/tenants/${tenantId}?force=true`, { method: "DELETE" }),
  addRentChange: (propertyId: string, tenantId: string, input: CreateRentChangeInput) =>
    api<RentChange>(`/realestate/${propertyId}/tenants/${tenantId}/rent-changes`, {
      method: "POST",
      body: input,
    }),
  updateRentChange: (
    propertyId: string,
    tenantId: string,
    rentChangeId: string,
    input: UpdateRentChangeInput,
  ) =>
    api<RentChange>(`/realestate/${propertyId}/tenants/${tenantId}/rent-changes/${rentChangeId}`, {
      method: "PATCH",
      body: input,
    }),
  deleteRentChange: (propertyId: string, tenantId: string, rentChangeId: string) =>
    api<void>(`/realestate/${propertyId}/tenants/${tenantId}/rent-changes/${rentChangeId}`, {
      method: "DELETE",
    }),

  // ---- RecurringCosts ----
  listRecurringCosts: (propertyId: string) =>
    api<{ recurringCosts: RecurringCost[] }>(`/realestate/${propertyId}/recurring-costs`),
  addRecurringCost: (propertyId: string, input: CreateRecurringCostInput) =>
    api<RecurringCost>(`/realestate/${propertyId}/recurring-costs`, {
      method: "POST",
      body: input,
    }),
  updateRecurringCost: (propertyId: string, costId: string, input: UpdateRecurringCostInput) =>
    api<RecurringCost>(`/realestate/${propertyId}/recurring-costs/${costId}`, {
      method: "PATCH",
      body: input,
    }),
  deleteRecurringCost: (propertyId: string, costId: string) =>
    api<void>(`/realestate/${propertyId}/recurring-costs/${costId}`, { method: "DELETE" }),

  // ---- OneTimeEntries ----
  listOneTimeEntries: (propertyId: string, query?: { from?: string; to?: string }) => {
    const sp = new URLSearchParams();
    if (query?.from) sp.set("from", query.from);
    if (query?.to) sp.set("to", query.to);
    const qs2 = sp.toString() ? `?${sp.toString()}` : "";
    return api<{ oneTimeEntries: OneTimeEntry[] }>(
      `/realestate/${propertyId}/one-time-entries${qs2}`,
    );
  },
  addOneTimeEntry: (propertyId: string, input: CreateOneTimeEntryInput) =>
    api<OneTimeEntry>(`/realestate/${propertyId}/one-time-entries`, {
      method: "POST",
      body: input,
    }),
  updateOneTimeEntry: (propertyId: string, entryId: string, input: UpdateOneTimeEntryInput) =>
    api<OneTimeEntry>(`/realestate/${propertyId}/one-time-entries/${entryId}`, {
      method: "PATCH",
      body: input,
    }),
  deleteOneTimeEntry: (propertyId: string, entryId: string) =>
    api<void>(`/realestate/${propertyId}/one-time-entries/${entryId}`, { method: "DELETE" }),
};

// ---- Vacancy ----

export type VacancyReason =
  | "RENOVIERUNG"
  | "KEINE_NACHFRAGE"
  | "MIETERWECHSEL"
  | "EIGENNUTZUNG"
  | "SONSTIGES";

export interface VacancyAnnotation {
  id: string;
  unitLabel: string;
  startMonth: string;
  reason: VacancyReason;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VacancyPeriod {
  unitLabel: string;
  startMonth: string;
  endMonth: string | null;
  durationMonths: number | null;
  sourcePrevTenantId: string | null;
  sourceNextTenantId: string | null;
  annotation: VacancyAnnotation | null;
}

export interface VacancyListResult {
  periods: VacancyPeriod[];
  orphanAnnotations: VacancyAnnotation[];
}

export interface VacancyStatsPerUnit {
  unitLabel: string;
  vacantMonths: number;
  availableMonths: number;
  vacancyRatePct: number;
}

export interface VacancyStats {
  year: number;
  perUnit: VacancyStatsPerUnit[];
  totalVacantMonths: number;
  totalAvailableMonths: number;
  totalVacancyRatePct: number;
}

export interface CreateVacancyAnnotationInput {
  unitLabel: string;
  startMonth: string;
  reason: VacancyReason;
  notes?: string;
}

export interface UpdateVacancyAnnotationInput {
  reason?: VacancyReason;
  notes?: string | null;
}

export const vacancyApi = {
  list: (propertyId: string, asOf?: string) =>
    api<VacancyListResult>(
      `/realestate/properties/${propertyId}/vacancies${asOf ? `?asOf=${asOf}` : ""}`,
    ),
  statistics: (propertyId: string, year: number, asOf?: string) =>
    api<VacancyStats>(
      `/realestate/properties/${propertyId}/vacancies/statistics?year=${year}${asOf ? `&asOf=${asOf}` : ""}`,
    ),
  create: (propertyId: string, input: CreateVacancyAnnotationInput) =>
    api<VacancyAnnotation>(`/realestate/properties/${propertyId}/vacancy-annotations`, {
      method: "POST",
      body: input,
    }),
  update: (annotationId: string, input: UpdateVacancyAnnotationInput) =>
    api<VacancyAnnotation>(`/realestate/vacancy-annotations/${annotationId}`, {
      method: "PATCH",
      body: input,
    }),
  remove: (annotationId: string) =>
    api<void>(`/realestate/vacancy-annotations/${annotationId}`, { method: "DELETE" }),
};
